import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Sparkles, Compass, Activity, Building2, TrendingUp,
  ShieldCheck, LineChart, Cpu, Briefcase, ArrowUpRight,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import usePageMeta from "@/hooks/usePageMeta";

const WHY = [
  { icon: Compass, t: "Decision Support", d: "Forensic-grade strategic intelligence built for board-level decisions." },
  { icon: Briefcase, t: "Competitive Intelligence", d: "Battlecards, share maps, win/loss playbooks — sourced and ready." },
  { icon: TrendingUp, t: "Growth Opportunity Mapping", d: "Where-to-play decision trees and quantified adjacency screens." },
  { icon: Cpu, t: "Technology Intelligence", d: "Patent landscapes, adoption curves, and tech-to-P&L impact models." },
  { icon: LineChart, t: "Market Intelligence", d: "Sizing, forecasting, and pricing — every model defensible." },
  { icon: ShieldCheck, t: "Strategic Advisory", d: "Integrated engagements that translate intelligence into action." },
];

function IndustryCard({ industry, idx }) {
  return (
    <Link to={`/industries/${industry.slug}`} className="group block bg-white border border-[var(--border)] hover-lift" data-testid={`industry-card-${industry.slug}`}>
      <div className="relative aspect-[16/10] overflow-hidden bg-[var(--navy)]">
        {industry.hero_image && (
          <img src={industry.hero_image} alt={industry.name} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-500" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy)]/70 via-transparent to-transparent" />
        <div className="absolute top-3 left-3 text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--gold)]">No. {String(idx + 1).padStart(2, "0")}</div>
        <div className="absolute bottom-3 left-3 right-3">
          <h3 className="font-display font-bold text-white text-2xl leading-tight">{industry.name}</h3>
        </div>
      </div>
      <div className="p-5">
        <p className="text-sm text-[var(--slate)] leading-relaxed">{industry.tagline}</p>
        <div className="mt-4 grid grid-cols-2 gap-3 text-[11px] font-mono uppercase">
          <div>
            <div className="text-[var(--slate-light)]">Mkt</div>
            <div className="text-[var(--navy)] text-base">${industry.market_size_usd_b}B</div>
          </div>
          <div>
            <div className="text-[var(--slate-light)]">CAGR</div>
            <div className="text-[var(--gold)] text-base">{industry.cagr_pct}%</div>
          </div>
        </div>
        <div className="mt-5 grid sm:grid-cols-2 gap-3 text-[12px]">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--gold)] mb-1">Opportunities</div>
            <ul className="space-y-0.5 text-[var(--slate)]">
              {(industry.key_opportunities || []).slice(0, 2).map((o, i) => (
                <li key={i} className="leading-snug">• {o}</li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--slate-light)] mb-1">Risks</div>
            <ul className="space-y-0.5 text-[var(--slate)]">
              {(industry.key_risks || []).slice(0, 2).map((o, i) => (
                <li key={i} className="leading-snug">• {o}</li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-5 pt-4 border-t border-[var(--border)] text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--navy)] flex items-center justify-between">
          <span>Intelligence Coverage</span>
          <ArrowUpRight size={12} className="text-[var(--gold)] group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </Link>
  );
}

function TrackerCard({ tracker }) {
  return (
    <Link to={`/trackers/${tracker.slug}`} className="group block bg-white border border-[var(--border)] p-6 hover-lift" data-testid={`tracker-card-${tracker.slug}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--gold)]">{tracker.tracker_kind} · {tracker.frequency}</div>
        <Activity size={14} className="text-[var(--gold)]" />
      </div>
      <h3 className="font-display font-bold text-xl mt-3 text-[var(--navy)] group-hover:text-[var(--gold)] transition-colors leading-tight">{tracker.title}</h3>
      <p className="mt-2 text-sm text-[var(--slate)] leading-relaxed">{tracker.blurb}</p>
      <div className="mt-5 grid grid-cols-2 gap-3">
        {(tracker.metrics || []).slice(0, 4).map((m, i) => (
          <div key={i} className="border-l-2 border-[var(--gold)] pl-3">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--slate-light)]">{m.label}</div>
            <div className="font-display font-bold text-lg text-[var(--navy)]">{m.value}</div>
            <div className="text-[10px] font-mono text-[var(--gold)]">{m.trend}</div>
          </div>
        ))}
      </div>
      <div className="mt-5 pt-4 border-t border-[var(--border)] text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--navy)] flex items-center gap-1">
        Open tracker <ArrowRight size={12} />
      </div>
    </Link>
  );
}

export default function HomePage() {
  usePageMeta({
    title: "Luminary Insights & Advisory — Strategic Intelligence for High-Growth Industries",
    description: "Strategic intelligence and advisory for boards, investors, and operating leaders. 14 industries · 15 modules · 7 frameworks · Luminous AI executive briefs.",
    path: "/",
    keywords: "strategic intelligence, executive decision intelligence, strategic advisory, industry trackers, decision intelligence framework",
  });

  const [industries, setIndustries] = useState([]);
  const [trackers, setTrackers] = useState([]);
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/industries").then((r) => setIndustries(r.data || []));
    api.get("/trackers").then((r) => setTrackers(r.data || []));
  }, []);

  const submitNewsletter = async (e) => {
    e.preventDefault();
    setMsg("");
    try {
      await api.post("/newsletter", { email, source: "homepage" });
      setMsg("Subscribed. The next intelligence brief arrives Monday.");
      setEmail("");
    } catch (err) { setMsg(formatApiError(err)); }
  };

  return (
    <PublicLayout>
      {/* SECTION 1 — HERO */}
      <section className="bg-white border-b border-[var(--border)] grain" data-testid="homepage-hero">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-16 sm:pb-24 grid lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-7 fade-up">
            <div className="eyebrow">Insights · Research · Advisory</div>
            <h1 className="font-display font-black text-[clamp(2.6rem,6.6vw,5.6rem)] leading-[0.98] tracking-tight mt-5 text-[var(--navy)]">
              Strategic Intelligence<br/>
              for <span className="italic font-medium text-[var(--gold)]">High-Growth Industries.</span>
            </h1>
            <p className="mt-7 text-lg leading-relaxed text-[var(--slate)] max-w-2xl">
              Strategic decision-making for boards, investors, and operating leaders — built on
              market intelligence, competitive intelligence, and advisory across 14 high-growth
              industries and the leading global participants shaping them.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3 sm:gap-4">
              <Link to="/strategy-session" className="btn-primary" data-testid="hero-cta-schedule">
                Schedule Strategy Session <ArrowRight size={16} />
              </Link>
              <Link to="/industries" className="btn-secondary" data-testid="hero-cta-industries">
                Explore Industry Intelligence
              </Link>
            </div>
            <div className="mt-10 pt-6 border-t border-[var(--border)] flex flex-wrap gap-x-6 gap-y-2 text-xs font-mono uppercase tracking-[0.18em] text-[var(--slate)]">
              <span>14 industries</span><span className="text-[var(--border-strong)]">·</span>
              <span>15 intelligence modules</span><span className="text-[var(--border-strong)]">·</span>
              <span>7 proprietary frameworks</span>
            </div>
          </div>

          <aside className="lg:col-span-5 fade-up-delay-1">
            <div className="bg-[var(--navy)] text-white p-8 sm:p-10 h-full flex flex-col">
              <div className="eyebrow text-[var(--gold)]">Why Luminary</div>
              <h3 className="font-display font-bold text-3xl sm:text-4xl mt-4 leading-tight">
                Where research meets strategy.
              </h3>
              <p className="mt-5 text-white/75 leading-relaxed text-[15px]">
                For leaders deciding capital allocation, market entry, M&amp;A, growth strategy, and
                technology bets — Luminary provides the intelligence that compresses decision cycles
                from months to days.
              </p>
              <div className="mt-8 pt-6 border-t border-white/15 grid grid-cols-3 gap-4">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--gold)]">Industries</div>
                  <div className="font-display font-black text-3xl mt-1">14</div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--gold)]">Trackers</div>
                  <div className="font-display font-black text-3xl mt-1">14</div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--gold)]">Modules</div>
                  <div className="font-display font-black text-3xl mt-1">15</div>
                </div>
              </div>
              <Link to="/luminous" className="mt-10 inline-flex items-center gap-2 text-[var(--gold)] font-mono text-xs uppercase tracking-[0.2em] hover:text-white">
                Try Luminous AI <ArrowRight size={12} />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* SECTION 2 — INDUSTRY COVERAGE */}
      <section className="border-b border-[var(--border)] bg-[var(--bg)] py-16 sm:py-24" data-testid="industries-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-10 sm:mb-14 gap-6">
            <div>
              <div className="eyebrow">Industry Coverage</div>
              <h2 className="font-display font-black text-[clamp(2rem,4.6vw,3.6rem)] mt-3 leading-[0.98] tracking-tight text-[var(--navy)]">
                Fourteen high-growth industries.<br />
                <span className="italic font-medium text-[var(--gold)]">One intelligence platform.</span>
              </h2>
            </div>
            <Link to="/industries" className="btn-secondary" data-testid="all-industries-link">All industries <ArrowRight size={14} /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6" data-testid="industries-grid">
            {industries.map((i, idx) => <IndustryCard key={i.slug} industry={i} idx={idx} />)}
          </div>
        </div>
      </section>

      {/* SECTION 3 — WHY LUMINARY */}
      <section className="border-b border-[var(--border)] bg-white py-16 sm:py-24" data-testid="why-luminary">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-4">
              <div className="eyebrow">Why Luminary</div>
              <h2 className="font-display font-black text-[clamp(2rem,4.4vw,3.4rem)] mt-3 leading-tight text-[var(--navy)]">
                We help leaders make better decisions.
              </h2>
              <p className="mt-5 text-[var(--slate)] text-lg leading-relaxed">
                Our practice combines specialty research, decision-grade intelligence, and proprietary frameworks — built for the way real boardroom decisions get made.
              </p>
              <Link to="/firm" className="mt-7 inline-flex btn-ghost">About the firm <ArrowRight size={12} /></Link>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)] border border-[var(--border)]">
              {WHY.map((w, i) => (
                <div key={i} className="bg-white p-6 sm:p-7" data-testid={`why-card-${i}`}>
                  <w.icon size={22} className="text-[var(--gold)]" />
                  <h3 className="font-display font-bold text-xl mt-5 text-[var(--navy)] leading-tight">{w.t}</h3>
                  <p className="mt-2 text-sm text-[var(--slate)] leading-relaxed">{w.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — INSIGHTS / TRACKERS */}
      <section className="border-b border-[var(--border)] bg-[var(--bg)] py-16 sm:py-24" data-testid="insights-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-10 sm:mb-14 gap-6">
            <div>
              <div className="eyebrow">Live Intelligence</div>
              <h2 className="font-display font-black text-[clamp(2rem,4.4vw,3.4rem)] mt-3 leading-tight text-[var(--navy)]">
                Trackers, not articles.
              </h2>
              <p className="mt-4 text-lg text-[var(--slate)] max-w-2xl">
                Living intelligence on capacity, capex, M&amp;A, patents, regulation, and technology — refreshed for decision-makers, not casual readers.
              </p>
            </div>
            <Link to="/trackers" className="btn-secondary" data-testid="all-trackers-link">All trackers <ArrowRight size={14} /></Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {trackers.slice(0, 6).map((t) => <TrackerCard key={t.slug} tracker={t} />)}
          </div>
          <div className="mt-10 text-center">
            <p className="text-[var(--slate-light)] font-display italic">Need deeper intelligence?</p>
            <Link to="/strategy-session" className="btn-primary mt-5 inline-flex" data-testid="insights-cta">Speak with Luminary <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      {/* SECTION 5 — LUMINOUS AI cutout */}
      <section className="terminal-bg" data-testid="luminous-teaser">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-20 sm:py-24 grid lg:grid-cols-12 gap-10 sm:gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow text-[var(--gold)]">Luminous AI</div>
            <h2 className="font-display font-black text-[clamp(2rem,5vw,4rem)] mt-4 leading-[1.05] tracking-tight text-white">
              Six executive briefs.<br/>
              <span className="italic font-medium text-[var(--gold)]">Sixty seconds each.</span>
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-white/75 max-w-xl">
              Not another chatbot. Structured strategic briefs — Company Intelligence,
              Market Attractiveness, Growth Opportunity, Competitive Positioning, Investment
              Thesis, and Executive Decision — delivered as board-ready one-pagers.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/luminous" className="btn-gold" data-testid="luminous-teaser-cta">
                Generate a brief <Sparkles size={14} />
              </Link>
              <Link to="/strategy-session" className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--gold)] hover:text-white inline-flex items-center gap-2">
                Or discuss with our advisory <ArrowUpRight size={12} />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="terminal-card p-6 sm:p-7">
              <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--gold)]">Sample · Market Attractiveness Snapshot</div>
              <div className="mt-3 font-display text-2xl font-bold text-white">Specialty Polymers · EV Batteries · NA · 5Y</div>
              <div className="my-5 h-px bg-white/10" />
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--gold)]">Verdict</div>
              <p className="mt-2 text-[15px] leading-relaxed text-white">Highly attractive. Score 78/100.</p>
              <div className="mt-5 text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--gold)]">Key Drivers</div>
              <p className="mt-2 text-[14px] leading-relaxed text-white/85">
                Strong growth (≥18% CAGR), high WTP, IRA tailwind. Watch supplier concentration in cathode-coating chemistry.
              </p>
              <div className="mt-5 flex items-center gap-3">
                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--gold)]">CONFIDENCE</span>
                <div className="confidence-bar flex-1"><span style={{ width: "80%" }} /></div>
                <span className="text-[11px] font-mono text-white">0.80</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6 — NEWSLETTER */}
      <section className="bg-[var(--bg)] py-20 border-b border-[var(--border)]">
        <div className="max-w-[860px] mx-auto px-4 sm:px-6 text-center">
          <div className="eyebrow">The Luminary Brief</div>
          <h2 className="font-display font-black text-[clamp(1.8rem,4.4vw,3.2rem)] mt-4 leading-[1.05] tracking-tight text-[var(--navy)]">
            Monday mornings. Three minutes.<br/>
            <span className="italic font-medium text-[var(--gold)]">The decisions the week demands.</span>
          </h2>
          <form onSubmit={submitNewsletter} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="newsletter-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="executive.address@firm.com"
              required
              className="input-editorial"
              data-testid="newsletter-email"
            />
            <button type="submit" className="btn-primary" data-testid="newsletter-submit">Subscribe</button>
          </form>
          {msg && <p className="mt-4 text-sm font-mono text-[var(--gold)]" data-testid="newsletter-msg">{msg}</p>}
        </div>
      </section>
    </PublicLayout>
  );
}
